import { useTranslation } from 'react-i18next';

import { Box, Drawer, Typography } from '@mui/material';

import type { ToastSeverity } from '../../lib/useToast';
import type { PriceLineResponse } from '../../model/types';
import type { PriceCellEditorContext, PriceCellEditorGroupContext } from './PriceLineEditorForm';
import { PriceLineEditorForm } from './PriceLineEditorForm';

export type { PriceCellEditorContext, PriceCellEditorGroupContext };

export interface PriceCellEditorDrawerProps {
  open: boolean;
  product: { id: string; name?: string };
  context: PriceCellEditorContext;
  existingLines?: PriceLineResponse[];
  /** See `PriceLineEditorForm`'s own doc comment (plan-merchant-portal-018 WP2.3) — forwarded
   * as-is; only the products-list quick-edit sets this. */
  storeCaption?: string;
  /** Forwarded to `PriceLineEditorForm` as-is (design spec §4 — a `catalog.pricing.view`-only
   * caller opens this shell read-only rather than not at all, plan-merchant-portal-018 WP2.3's
   * gate fix). Defaults `false`, byte-identical to every pre-existing caller. */
  readOnly?: boolean;
  onClose: () => void;
  onResult: (severity: ToastSeverity, message: string) => void;
}

/**
 * Right-side drawer to replace / schedule a governed price line for a (product, slot) — the thin
 * shell adr-merchant-portal-018 Decision 1 leaves in place for `PriceGridPanel`, the price-list
 * detail page and the products-list `QuickPriceDialog`, kept byte-identical to before the
 * extraction (regression-pinned by this component's own, unmodified test suite). All editing logic
 * (seeding, validation, cost/margin, the submit itself) now lives in `PriceLineEditorForm`; this
 * shell only owns the `Drawer` chrome, the header (product name + the per-context slot identity —
 * unchanged from before the extraction) and translating the form's two-callback contract
 * (`onSaved`/`onCancel`) back into this component's own shipped `onResult`/`onClose` contract.
 */
export function PriceCellEditorDrawer({
  open,
  product,
  context,
  existingLines,
  storeCaption,
  readOnly = false,
  onClose,
  onResult,
}: PriceCellEditorDrawerProps) {
  const { t } = useTranslation('catalog');

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => onClose()}
      slotProps={{
        paper: {
          sx: {
            width: { xs: '100%', sm: 420 },
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      {/* Header */}
      <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
          {product.name ?? product.id}
        </Typography>
        {context.type === 'channel' ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {context.name ?? context.code}
          </Typography>
        ) : null}
        {context.type === 'list' ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('priceCellEditorDrawer.listContext', { name: context.name })}
          </Typography>
        ) : null}
        {context.type !== 'list' && context.group ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('priceCellEditorDrawer.groupContext', { name: context.group.name ?? context.group.code })}
          </Typography>
        ) : null}
      </Box>

      {open ? (
        <PriceLineEditorForm
          product={product}
          context={context}
          existingLines={existingLines}
          storeCaption={storeCaption}
          readOnly={readOnly}
          onSaved={(message) => {
            onResult('success', message);
            onClose();
          }}
          onCancel={onClose}
        />
      ) : null}
    </Drawer>
  );
}
